import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { Seo } from "@/components/Seo";
import { Reveal, Chapter } from "@/components/motion";

const orgProjects = [
  "Closets & wardrobes",
  "Kitchens & pantries",
  "Home offices",
  "Playrooms",
  "Bathrooms",
  "Whole-home organization",
  "Decluttering projects",
];

const conciergeProjects = [
  "Errands & everyday tasks",
  "Household projects & coordination",
  "The projects you keep postponing",
  "Busy seasons & life transitions",
];

const ServiceChapter = ({ number, overline, title, copy, items, image, alt, flip, note }) => (
  <section className={`py-24 lg:py-28 ${flip ? "bg-white" : ""}`} data-testid={`services-${number === "01" ? "organization" : "concierge"}`}>
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
      <Reveal className={flip ? "lg:order-2" : ""}>
        <div className="relative">
          <div className={`absolute h-full w-full rounded-3xl border border-gold/50 ${flip ? "-right-4 -top-4" : "-left-4 -top-4"}`} aria-hidden="true" />
          <img
            src={image}
            alt={alt}
            loading="lazy"
            className="relative aspect-[4/3] w-full rounded-3xl object-cover shadow-[0_24px_60px_rgba(26,46,68,0.16)]"
          />
        </div>
      </Reveal>
      <Reveal delay={0.12} className={flip ? "lg:order-1" : ""}>
        <Chapter number={number} label={overline} />
        <h2 className="mt-6 font-serif text-4xl sm:text-5xl leading-[1.08] text-navy">{title}</h2>
        <p className="mt-7 max-w-lg leading-relaxed text-navy/70">{copy}</p>
        <ul className="mt-8 grid gap-3.5 sm:grid-cols-2">
          {items.map((item) => (
            <li key={item} className="flex items-start gap-4 text-navy/80">
              <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
              <span className="leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
        <p className="mt-8 max-w-lg font-serif text-xl italic leading-snug text-gold-dark">{note}</p>
      </Reveal>
    </div>
  </section>
);

export default function Services() {
  return (
    <>
      <Seo
        title="Services | Professional Organizer & Personal Concierge in New Orleans"
        description="Professional organization and personal concierge services for Greater New Orleans. Closets, kitchens, home offices, playrooms, bathrooms, errands and everyday projects — $85/hour. Porter New Orleans."
        path="/services"
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-16 pt-16 sm:pt-24" data-testid="services-hero">
        <Reveal y={16}>
          <p className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-gold-dark">
            <span className="h-px w-8 bg-gold" aria-hidden="true" /> Services
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-7 max-w-4xl font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-navy">
            Whatever is weighing on you, <em className="text-gold-dark">hand it over.</em>
          </h1>
          <p className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-navy/70">
            Porter New Orleans focuses on two things done exceptionally well: professional organization
            and personal concierge support — for busy households across Greater New Orleans.
          </p>
        </Reveal>
      </section>

      <ServiceChapter
        number="01"
        overline="Professional Organization"
        title="Calm, functional spaces — that stay that way."
        copy="Some projects are too big, too emotional, or too easy to postpone. Porter tackles clutter and organization projects with a clear plan and a steady hand, creating spaces your household can actually maintain."
        items={orgProjects}
        image={IMAGES.workPantryWalkin}
        alt="A walk-in pantry organized by Porter New Orleans, with labeled bins and baskets"
        note="Every home is different — tell us about yours."
      />

      <ServiceChapter
        number="02"
        overline="Personal Concierge"
        title="Your to-do list, personally handled."
        copy="Life's logistics add up. Porter provides personalized assistance with everyday tasks, projects, and responsibilities, so your limited free time goes to the people and things you love — not the errands."
        items={conciergeProjects}
        image={IMAGES.workFridge}
        alt="A client's refrigerator stocked and organized by Porter New Orleans before their arrival"
        note="Have something specific in mind? If it makes your life easier, it's worth asking."
        flip
      />

      <section className="bg-navy py-20 lg:py-24" data-testid="services-rate-band">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_auto]">
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-gold">How it works</p>
            <p className="mt-5 font-serif text-4xl sm:text-5xl text-cream">
              Simple hourly rate — <span className="text-gold-light" data-testid="services-rate">$85/hour</span>
            </p>
            <p className="mt-5 max-w-xl leading-relaxed text-cream/70">
              Every engagement starts with a simple conversation about what you need. No complicated
              packages, no pressure — just a clear hourly rate and a plan that fits your life.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <Link
              to="/contact"
              data-testid="services-cta-button"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-9 py-4 text-sm font-bold tracking-wide text-navy-dark transition-all duration-300 hover:bg-gold-light hover:-translate-y-0.5"
            >
              Get in Touch <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
